/**
 * ContactPicker — searchable single-select for choosing a contact to
 * use as the internal-CID source on extensions / ring groups
 * (migration 0085). Mirrors the TimeFramePicker shape:
 *   - "None" option clears the selection
 *   - Empty state with a "Manage contacts" link when the customer
 *     has no contacts yet
 *
 * Per CLAUDE.md rule 13: never use a raw UUID text input. This
 * picker is the canonical way to link a contact to an extension or
 * ring group from the portal.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { contacts, type ContactRow } from '@/api/contacts';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { UserSquare, ExternalLink } from 'lucide-react';

const NONE_VALUE = '__none__';

export function ContactPicker({
  value,
  onChange,
  label,
  emptyHint,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  label?: string;
  emptyHint?: string;
}) {
  const customerId = useEffectiveCustomerId();
  const { data, isLoading } = useQuery({
    queryKey: ['contacts', 'picker', customerId],
    queryFn: () => contacts.list({
      customer_id: customerId ?? undefined,
      active_only: true,
      limit: 500,
    }),
  });
  const rows: ContactRow[] = data?.rows ?? [];
  const selected = rows.find((r) => r.id === value) ?? null;

  // Empty state — link the operator to the contacts page so they can
  // add one without leaving the editor's mental model. Matches
  // TimeFramePicker / AuthListPicker conventions.
  if (!isLoading && rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border bg-secondary/20 p-3 space-y-1.5">
        <p className="text-xs text-muted-foreground">
          {emptyHint ?? 'No contacts have been created yet for this customer.'}
        </p>
        <Link
          to="/contacts"
          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Manage contacts
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      {label && (
        <label className="text-xs font-medium text-muted-foreground block">
          {label}
        </label>
      )}
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(v) => onChange(v === NONE_VALUE ? null : v)}
      >
        <SelectTrigger className="w-full h-8 text-sm">
          <SelectValue placeholder="No contact linked" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>
            <span className="text-muted-foreground">— None (use override / external CID) —</span>
          </SelectItem>
          {rows.map((r) => (
            <SelectItem key={r.id} value={r.id}>
              <span className="inline-flex items-center gap-2">
                <UserSquare className="h-3.5 w-3.5 opacity-60" />
                <span>{r.display_name}</span>
                <span className="font-mono text-[10px] text-muted-foreground">
                  CT-{r.ref_number}
                </span>
                {r.internal_cid_phone && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    {r.internal_cid_phone}
                  </span>
                )}
                {!r.active && <Badge variant="secondary" className="text-[10px]">inactive</Badge>}
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {selected && (
        <p className="text-[11px] text-muted-foreground leading-snug">
          Internal calls will show{' '}
          <span className="font-medium">{selected.display_name}</span>
          {selected.internal_cid_phone && (
            <> ({selected.internal_cid_phone})</>
          )}
          {' '}on the called party's handset.{' '}
          <Link
            to="/contacts"
            className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-0.5"
          >
            Edit <ExternalLink className="h-2.5 w-2.5" />
          </Link>
        </p>
      )}
    </div>
  );
}
